/**
 * Prompt Enhancer Service
 * 
 * Calls the prompt enhancer API which:
 * 1. Enhances the prompt (creates positive and negative prompts)
 * 2. Writes enhanced prompts + image_url to Supabase
 * 3. Returns enhanced prompts for use in video generation
 * 
 * Note: This service only enhances prompts. Video generation must be triggered
 * separately by calling the video generation API with the enhanced prompts.
 */

import { PROMPT_ENHANCER_CONFIG, API_ENDPOINTS } from '../utils/constants';

export interface PromptEnhancerRequest {
  image_url: string; // Image URL (required)
  prompt: string; // Original prompt (required)
  asset_id: string; // Asset ID from Supabase (required for enhance-from-supabase endpoint)
}

export interface PromptEnhancerResponse {
  prompt: string; // Enhanced prompt (matches fal.ai and industry standard)
  negative_prompt: string; // Enhanced negative prompt
  image_url: string; // Image URL (same as input)
  video_job_id?: string; // Optional video job ID from prompt enhancer (if it starts generation)
}

/**
 * Enhance prompt using the prompt enhancer API
 *
 * Makes a single attempt. The timeoutMs parameter controls both the frontend
 * AbortController timeout AND is sent to the backend to control the Together AI
 * call timeout. No retries — the caller (useVideoGeneration) falls back to the
 * original prompt if this fails.
 *
 * @param request - Request with image_url and prompt
 * @param timeoutMs - Timeout in milliseconds. Controls frontend abort + backend Together AI timeout.
 *                     Lander-1 uses 10_000 (10s), Homepage uses 60_000 (60s).
 * @returns Enhanced prompts (prompt, negative_prompt) and image_url
 */
export async function enhancePrompt(
  request: PromptEnhancerRequest,
  timeoutMs?: number,
): Promise<PromptEnhancerResponse> {
  // Validate required fields - asset_id must be a valid Supabase asset ID
  if (!request.asset_id || !request.asset_id.trim()) {
    throw new Error('Asset ID is required for prompt enhancement (Supabase asset ID)');
  }
  
  // Validate asset_id format (Supabase uses UUID format)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(request.asset_id.trim())) {
    console.warn('⚠️ [PromptEnhancer] Asset ID does not match UUID format:', request.asset_id);
    // Don't fail - backend will validate, but log warning
  }
  
  const baseUrl = PROMPT_ENHANCER_CONFIG.BASE_URL;
  const endpoint = API_ENDPOINTS.PROMPT_ENHANCER.ENHANCE_AND_GENERATE;

  // Always send prompt in request body - backend should use this as fallback
  // if it fails to get prompt from Supabase asset record
  const requestBody: Record<string, any> = {
    image_url: request.image_url,
    prompt: request.prompt, // Always included - backend should use this if asset record doesn't have it
    asset_id: request.asset_id, // Required for enhance-from-supabase endpoint
  };

  // Send timeout to backend (in seconds) so it can control the Together AI call duration
  if (timeoutMs) {
    requestBody.timeout = Math.round(timeoutMs / 1000);
  }

  // Single attempt, no retries. Frontend AbortController provides a safety-net timeout.
  // The backend also uses the timeout value for the Together AI call.
  // useVideoGeneration falls back to the original prompt if this fails.
  const controller = timeoutMs ? new AbortController() : undefined;
  const abortTimer = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : undefined;

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
      keepalive: true,
      signal: controller?.signal,
    });
  } catch (error: any) {
    if (error.name === 'AbortError') {
      throw new Error(`Prompt enhancement timed out after ${timeoutMs! / 1000}s`);
    }
    throw new Error(`Network error: ${error.message || 'Failed to connect to prompt enhancer'}`);
  } finally {
    if (abortTimer) clearTimeout(abortTimer);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to enhance prompt' }));

    // 402 = insufficient credits — throw structured error, don't fall back
    if (response.status === 402) {
      throw {
        type: 'insufficient_credits' as const,
        message: error.message || error.error || 'Insufficient credits',
        current_credits: error.current_credits ?? 0,
        required_credits: error.required_credits ?? 100,
      };
    }

    throw new Error(error.error || error.message || 'Failed to enhance prompt');
  }

  const result = await response.json();
  
  // Handle different field names from backend (enhanced_prompt, prompt, positive_prompt)
  const enhancedPrompt = result.prompt || result.enhanced_prompt || result.positive_prompt;
  
  if (!enhancedPrompt) {
    console.error('❌ [PromptEnhancer] No prompt field found in response. Available fields:', Object.keys(result));
    console.error('❌ [PromptEnhancer] Full response:', result);
    throw new Error('Prompt enhancer did not return an enhanced prompt');
  }
  
  return {
    prompt: enhancedPrompt,
    negative_prompt: result.negative_prompt,
    image_url: result.image_url,
    video_job_id: result.video_job_id, // Include video_job_id if provided by backend
  };
}


